import { ErrorReportingClient } from '../types/ErrorReportingClient';
import { ErrorReportingDevtools } from '../types/ErrorReportingDevtools';
import { triggerRenderError } from './renderErrorEmitter';

/**
 * Fires a test error of the requested kind. Each kind exercises a
 * different path the error-reporting client must support - synchronous
 * throws, async throws, unhandled promise rejections, console.error,
 * direct exception capture, message capture, and React render-time
 * throws - so QA can verify they all reach the dashboard with the
 * expected context attached.
 */
export const createTriggerErrorDevtool = (
  client: ErrorReportingClient,
): ErrorReportingDevtools['triggerError'] => {
  return (kind, message): void => {
    const resolvedKind = kind ?? 'throw';
    const finalMessage =
      message ?? `Test error (${resolvedKind}) from errorReporting devtools`;
    const error = new Error(finalMessage);

    switch (resolvedKind) {
      case 'async-throw':
        (async (): Promise<void> => {
          await Promise.resolve();
          throw error;
        })();
        return;
      case 'unhandled-promise':
        // Intentionally unhandled - captured by the client's global handler.
        Promise.reject(error);
        return;
      case 'console-error':
        console.error(finalMessage, error);
        return;
      case 'manual-exception':
        client.captureException(error, {
          extra: { source: 'errorReporting.triggerError' },
          tags: { triggered: 'true', triggeredKind: resolvedKind },
        });
        return;
      case 'manual-message':
        client.captureMessage(finalMessage, {
          extra: { source: 'errorReporting.triggerError' },
          level: 'error',
          tags: { triggered: 'true', triggeredKind: resolvedKind },
        });
        return;
      case 'render-crash':
        // Forces a render-time throw inside the outer React error boundary
        // so QA can verify component-stack capture.
        triggerRenderError(finalMessage);
        return;
      case 'throw':
      default:
        // Defer so the throw escapes the synchronous caller (ex. a console
        // invocation) and is reported by the global error handler.
        setTimeout((): void => {
          throw error;
        }, 0);
    }
  };
};
